import React from 'react';
import { Box, Typography } from '@mui/material';

export interface TimeDisplayProps {
  currentTime: Date;
  useUTCTime: boolean;
}

const TimeDisplay: React.FC<TimeDisplayProps> = ({ currentTime, useUTCTime }) => {
  const formatDisplayTime = (date: Date): string => {
    if (useUTCTime) {
      return date.toISOString().slice(0, 16).replace('T', ' ') + ' UTC';
    } else {
      // Format for local time display - get the local time representation
      const localDate = new Date(date.getTime());
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      const hours = String(localDate.getHours()).padStart(2, '0');
      const minutes = String(localDate.getMinutes()).padStart(2, '0');
      
      // Get timezone abbreviation, but never show "UTC" in local mode
      try {
        const timeZoneFormatter = new Intl.DateTimeFormat('en', {
          timeZoneName: 'short'
        });
        const timeZoneParts = timeZoneFormatter.formatToParts(localDate);
        let timeZone = timeZoneParts.find(part => part.type === 'timeZoneName')?.value || 'Local';
        
        // If timezone is UTC but we're in local mode, force it to show as Local
        if (timeZone === 'UTC') {
          timeZone = 'Local';
        }
        
        return `${year}-${month}-${day} ${hours}:${minutes} ${timeZone}`;
      } catch (error) {
        // Fallback if Intl.DateTimeFormat fails
        return `${year}-${month}-${day} ${hours}:${minutes} Local`;
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        Custom Date/Time:
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
        {formatDisplayTime(currentTime)}
      </Typography>
    </Box>
  );
};

export default TimeDisplay;